import Main from "niteroi/packages/air-passenger-rights/controllers/Main.controller";
import {SearchField$LiveChangeEvent} from "sap/m/SearchField";
import Table from "sap/m/Table";
import Filter from "sap/ui/model/Filter";
import FilterOperator from "sap/ui/model/FilterOperator";
import ListBinding from "sap/ui/model/ListBinding";
import {ListItemBase$PressEvent} from "sap/m/ListItemBase";
import {Route$PatternMatchedEvent} from "sap/ui/core/routing/Route";
import {Button$PressEvent} from "sap/m/Button";
import SimpleForm from "sap/ui/layout/form/SimpleForm";
import Toolbar from "sap/m/Toolbar";
import Page from "sap/m/Page";
import Ui5File from "sap/ui/core/util/File";
import {ILoadDataResponse} from "niteroi/packages/system/models/interfaces";

export default class Airlines extends Main {
    private readonly onPatternMatchedHandler = this.onPatternMatched.bind(this);

    public onInit(): void {
        super.onInit();
        const router = this.getRouter();
        const routeName = this.getCurrentRouteName(router);
        if (routeName) {
            const route = router.getRoute(routeName);

            if (route.getPattern().includes("airlines/add")) {
                this.resetOriginalModel("airline");
            } else {
                route.detachPatternMatched(this.onPatternMatchedHandler);
                route.attachPatternMatched(this.onPatternMatchedHandler);
            }
        }
    }

    public onBeforeRendering(): void {
        this.setActiveTileByRoute();
    }

    protected onPatternMatched(event: Route$PatternMatchedEvent): void {
        this.resetOriginalModel("airline");
        const params = event.getParameters();
        const id = (params.arguments as { id: string }).id ?? "";
        this.getData(id);
    }

    protected getData(objectId: string = ""): void {
        const url = objectId ? `/api/air-passenger-rights/airlines/${objectId}` : "/api/air-passenger-rights/airlines";
        const modelName = objectId ? "airline" : "airlines";
        this.fetchData(url, modelName, objectId);
    }

    /**
     * Search handler for airlines table
     * Filters airlines by IATA code, ICAO code, name, country, callsign, and alias
     * @param event - The search event containing the search query
     */
    public onListSearch(event: SearchField$LiveChangeEvent): void {
        const searchQuery = event.getParameter("newValue");
        const airlinesTable = this.byId("idAirlinesTable") as Table;

        if (!airlinesTable) {
            return;
        }

        const binding = airlinesTable.getBinding("items") as ListBinding;
        if (!binding) {
            return;
        }

        // If search query is empty, reset to default filters
        if (!searchQuery || searchQuery.trim() === "") {
            binding.filter([]);
            return;
        }

        // Create filters for each searchable field with case-insensitive Contains
        const searchFilters: Filter[] = [
            new Filter("iataCode", FilterOperator.Contains, searchQuery),
            new Filter("icaoCode", FilterOperator.Contains, searchQuery),
            new Filter("name", FilterOperator.Contains, searchQuery),
            new Filter("country", FilterOperator.Contains, searchQuery),
            new Filter("callsign", FilterOperator.Contains, searchQuery),
            new Filter("alias", FilterOperator.Contains, searchQuery)
        ];

        // Combine all search filters with OR logic
        const combinedSearchFilter = new Filter({
            filters: searchFilters,
            and: false // OR condition - match any field
        });

        // Keep the base filters for valid codes (AND with search)
        const baseFilters = [
            new Filter("iataCode", FilterOperator.NE, null),
            new Filter("icaoCode", FilterOperator.NE, null)
        ];

        // Combine base filters with search filter using AND logic
        const finalFilters = [
            ...baseFilters,
            combinedSearchFilter
        ];

        binding.filter(finalFilters);
    }

    public onPressColumnListItemDetail(event: ListItemBase$PressEvent): void {
        this.resetOriginalModel("airline");
        const source = event.getSource();
        const bindingContext = source.getBindingContext("airlines");
        const selectedListItem = bindingContext.getModel().getProperty(bindingContext.getPath());
        this.navTo("air-passenger-rights/airlines/edit", {id: selectedListItem.airlineId}, false);
    }

    public onPressButtonDiscardFormEdit(): void {
        this.discardChanges("airline");
    }

    public onPressMenuItemAdd(): void {
        this.resetOriginalModel("airline");
        this.navTo("air-passenger-rights/airlines/add", {}, false);
    }

    public onPressButtonSaveFormEdit(event: Button$PressEvent): void {
        const source = event.getSource();
        const page = ((source.getParent() as Toolbar).getParent() as SimpleForm).getParent() as Page;
        page.setBusy(true);
        const model = this.getModel("airline");
        if (JSON.stringify(this.originalData) !== model.getJSON()) {
            const airlineId = model.getProperty("/airlineId") as string;
            const method = airlineId ? "PUT" : "POST";
            const requestModel = this.createRequestModel();
            const url = airlineId ? `/api/air-passenger-rights/airlines/${airlineId}` : "/api/air-passenger-rights/airlines";
            const headers = this.getRequestHeaders();
            requestModel.loadData(url, model.getJSON(), true, method, false, true, headers).then(() => {
                model.setData(JSON.parse(requestModel.getJSON()));
                this.showToast(this.getText("msgAirlineSavedSuccess"));
            }).catch((response: ILoadDataResponse) => {
                const responseText = JSON.parse(response.responseText);
                this.showToast(`${responseText.code} - ${responseText.error}`);
            }).finally(() => {
                page.setBusy(false);
            });
        } else {
            page.setBusy(false);
        }
    }

    public onPressMenuItemExportJson(): void {
        this.exportToJSON("airlines");
    }

    public onPressDownloadTemplateBtn(): void {
        // Template based on Airlines structure
        const template = [{
            "iataCode": "LH",
            "icaoCode": "DLH",
            "name": "Lufthansa",
            "callsign": "LUFTHANSA",
            "country": "Germany",
            "continentCode": "EU",
            "alias": "",
            "isActive": true,
            "logo": "",
            "website": "https://www.lufthansa.com",
            "email": "",
            "phone": "+49 69 86 799 799"
        }];
        Ui5File.save(JSON.stringify(template, null, 4), "airlines_template", "json", "application/json", "utf-8");
    }
}
